import { useState, useEffect } from 'react';
import { useSeller } from '@/components/layout/DashboardLayout';
import { useProfile } from '@/lib/profile-context';
import { sellersAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { ShieldAlert, Clock, ArrowRight } from 'lucide-react';

function getSeverityStyle(severity) {
  if (severity === 'Critical') return { dot: 'bg-[hsl(0,84%,60%)]', badge: 'bg-[hsl(0,84%,60%)]/15 text-[hsl(0,84%,60%)] border-[hsl(0,84%,60%)]/30' };
  if (severity === 'High') return { dot: 'bg-[hsl(24,94%,56%)]', badge: 'bg-[hsl(24,94%,56%)]/15 text-[hsl(24,94%,56%)] border-[hsl(24,94%,56%)]/30' };
  return { dot: 'bg-[hsl(45,93%,58%)]', badge: 'bg-[hsl(45,93%,58%)]/15 text-[hsl(45,93%,58%)] border-[hsl(45,93%,58%)]/30' };
}

export default function AlertsPage() {
  const { hasSelection } = useProfile();
  const { currentSellerId, syncVersion, syncing } = useSeller();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [severityFilter, setSeverityFilter] = useState('all');

  useEffect(() => {
    if (!currentSellerId) { setLoading(false); return; }
    const load = async () => {
      setLoading(true);
      try {
        const sev = severityFilter === 'all' ? undefined : severityFilter;
        const res = await sellersAPI.getAlerts(currentSellerId, sev);
        setAlerts(res.data);
      } catch (err) {
        console.error('Failed to load alerts:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentSellerId, severityFilter, syncVersion]);

  if (loading || (hasSelection && syncing)) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24" />)}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="alerts-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Alerts</h1>
          <p className="text-sm text-muted-foreground">Triggered alerts sorted by recency</p>
        </div>
        <Select value={severityFilter} onValueChange={setSeverityFilter} data-testid="alerts-filter-select">
          <SelectTrigger className="w-[160px] h-9 text-sm bg-muted/40 border-border/60">
            <SelectValue placeholder="Filter severity" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Severities</SelectItem>
            <SelectItem value="Critical">Critical</SelectItem>
            <SelectItem value="High">High</SelectItem>
            <SelectItem value="Medium">Medium</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {alerts.length === 0 && (
        <Card className="bg-card border-border/80">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <ShieldAlert className="w-12 h-12 text-muted-foreground/50 mb-4" />
            <p className="text-sm text-muted-foreground">No alerts triggered</p>
          </CardContent>
        </Card>
      )}

      <div className="space-y-3" data-testid="alerts-panel">
        {alerts.map((alert) => {
          const style = getSeverityStyle(alert.severity);
          const time = new Date(alert.timestamp);
          return (
            <Card key={alert.id} className="bg-card border-border/80 hover:border-border transition-colors">
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <span className={cn('w-3 h-3 rounded-full mt-1 flex-shrink-0', style.dot)} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-sm font-medium">{alert.metric_name}</h3>
                      <Badge variant="outline" className={cn('text-[10px] px-1.5 py-0 border', style.badge)}>
                        {alert.severity}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Triggered at <span className="font-medium text-foreground">{alert.triggered_value}</span> (threshold: {alert.threshold})
                    </p>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                      <Clock className="w-3 h-3" />
                      {time.toLocaleDateString()} {time.toLocaleTimeString()}
                    </div>
                    <div className="mt-3 p-3 rounded-lg bg-muted/20 border border-border/40">
                      <div className="flex items-center gap-1 text-xs font-medium text-muted-foreground mb-1">
                        <ArrowRight className="w-3 h-3" /> Recommended Action
                      </div>
                      <p className="text-sm">{alert.recommended_action}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
